import { slugFromPathname } from "../tenant-paths.ts";
import { RequestError } from "./client.ts";

// Tracks whether we have ever seen a live session in this page lifetime. We only
// auto-redirect to sign-in when a *previously authenticated* session expires — a
// first-time visitor who is simply not signed in keeps the manual sign-in screen.
let wasAuthenticated = false;
// Latch so several concurrent 401s (e.g. a page with multiple queries) trigger at
// most one navigation.
let redirecting = false;

export const markAuthenticated = (): void => {
  wasAuthenticated = true;
};

// Suppress the session-expiry redirect for the rest of this page lifetime. Used
// by sign-out: it deletes the cookie, so an in-flight poll can 401 before the
// manual navigation lands — without this the expiry handler would bounce the
// user back into sign-in (silently re-authenticating) and defeat the logout.
export const suppressSessionRedirect = (): void => {
  redirecting = true;
};

// Test-only: reset module state between cases.
export const __resetAuthRedirect = (): void => {
  wasAuthenticated = false;
  redirecting = false;
};

export const isSessionRedirectSuppressed = (): boolean => redirecting;

export const isUnauthorized = (error: unknown): boolean => error instanceof RequestError && error.status === 401;

type Location = { pathname: string; search: string };

const TRANSIENT_PARAMS: readonly string[] = ["login_error", "signed_out"];

// Sign-in feedback params must not follow the user into returnTo.
const withoutTransientParams = (search: string): string => {
  const params = new URLSearchParams(search);
  for (const name of TRANSIENT_PARAMS) params.delete(name);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};

// Build the tenant sign-in URL, preserving the current path+query as `returnTo` so the
// user resumes where they left off after re-authenticating. Paths outside a tenant have no tenant to sign in to.
export const buildLoginUrl = (loc: Location): string => {
  const slug = slugFromPathname(loc.pathname);
  if (!slug) return "/auth/login";
  const returnTo = `${loc.pathname}${withoutTransientParams(loc.search)}`;
  return `/auth/login?${new URLSearchParams({ tenant: slug, returnTo })}`;
};

// Redirect to sign-in when a previously-active session has expired (401).
// Returns true when a navigation was triggered. No-op for first-load 401s (never
// authenticated), non-401 errors, and repeat calls after the first redirect.
export const handleSessionExpiry = (error: unknown, loc: Location, navigate: (url: string) => void): boolean => {
  if (redirecting || !wasAuthenticated || !isUnauthorized(error)) return false;
  redirecting = true;
  navigate(buildLoginUrl(loc));
  return true;
};
